import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Typography, CircularProgress, TextField } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import { getAccidentsTableColumns } from "../../../../data/FormColumns/Fleet/accidentsTableColumns";
import PreviewGalleryModal from "../PreviewGalleryModal/PreviewGalleryModal";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import PersistedDataGrid from "../../../PersistedDataGrid/PersistedDataGrid";

const fetchAccidents = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/accidents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchDrivers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((driver: any) => ({
    label: `${driver.firstName} ${driver.lastName} | ${driver.phoneNumber}`,
    id: driver.id.toString(),
  }));
};

const fetchTrucks = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrucks`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((truck: any) => ({
    label: `${truck.unitNumber} | ${truck.make} ${truck.model} ${truck.year}`,
    id: truck.id.toString(),
  }));
};

const fetchTrailers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrailers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((trailer: any) => ({
    label: `${trailer.unitNumber} | ${trailer.make} ${trailer.model} ${trailer.year}`,
    id: trailer.id.toString(),
  }));
};

const fetchDispatchers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllDispatchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data.map((dispatcher: any) => ({
    label: `${dispatcher.firstName} ${dispatcher.lastName} | ${dispatcher.email}`,
    id: dispatcher.id.toString(),
  }));
};

const AccidentsTable: React.FC = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [selectedAccident, setSelectedAccident] = useState<any | null>(null);
  const [fileUploadOpen, setFileUploadOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Prefetch related data
  React.useEffect(() => {
    if (token) {
      queryClient.prefetchQuery({
        queryKey: ["drivers"],
        queryFn: () => fetchDrivers(token),
      });
      queryClient.prefetchQuery({
        queryKey: ["trucks"],
        queryFn: () => fetchTrucks(token),
      });
      queryClient.prefetchQuery({
        queryKey: ["trailers"],
        queryFn: () => fetchTrailers(token),
      });
      queryClient.prefetchQuery({
        queryKey: ["dispatchers"],
        queryFn: () => fetchDispatchers(token),
      });
    }
  }, [token, queryClient]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["accidents"],
    queryFn: () => fetchAccidents(token),
    enabled: !!token,
  });

  const filteredAccidents = useMemo(() => {
    if (!data) return [];

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const filtered = data.filter((accident: any) => {
      if (!normalizedSearchTerm) return true;
      const searchFields = [
        accident.typeOfIncident,
        accident.location,
        accident.driverName,
        accident.broker,
        accident.claimNumber,
        accident.truckUnitNumber,
        accident.trailerNumber,
      ].map((field) => field?.toString().toLowerCase() || "");
      return searchFields.some((field) => field.includes(normalizedSearchTerm));
    });
    return filtered;
  }, [data, searchTerm]);

  const handleUpload = (accidentRow: any) => {
    setSelectedAccident(accidentRow);
    setFileUploadOpen(true);
  };

  const handlePreview = (accidentRow: any) => {
    setSelectedAccident(accidentRow);
    setPreviewOpen(true);
  };

  const columns = getAccidentsTableColumns(handleUpload, handlePreview);

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Type, Location, Driver, Broker, Claim, Truck or Trailer"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="accidents"
          rows={filteredAccidents}
          columns={columns}
          compactViewConfig={{
            primaryField: "typeOfIncident",
            secondaryFields: ["driverName", "date"],
            detailFields: [
              "ownerCompanyName",
              "description",
              "location",
              "truckUnitNumber",
              "trailerNumber",
              "dispatcherName",
              "claimNumber",
              "claimAmount",
              "insurance",
              "broker",
              "adjuster",
            ],
            actionFields: ["update", "notes", "actions", "Delete"],
            statusField: "status",
          }}
          sx={{ height: 700 }}
        />
      )}

      {selectedAccident && (
        <FileUploadModal
          open={fileUploadOpen}
          onClose={() => setFileUploadOpen(false)}
          accident={selectedAccident}
        />
      )}

      {selectedAccident && (
        <PreviewGalleryModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          accident={selectedAccident}
        />
      )}
    </Box>
  );
};

export default AccidentsTable;
